import { Injectable, OnDestroy, inject } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import {
  ClientWireApiClient,
  CONNECTED_EVENT,
  DISCONNECTED_EVENT,
  AUTHENTICATION_ERROR_EVENT,
  ConversationTypesApi,
  ConversationsApi,
  GeocodingApi,
  MessagesApi,
  ParticipantsApi,
  SMSSettingsApi,
  OIDCConfigsApi,
  APIKeysApi,
  SignupApi,
  TenantConfigApi,
  TenantsApi,
  UsersApi,
  TokenResponseDto,
  WsNewMessage,
  WsMessageUpdated,
  WsParticipantReadStatus,
  WsParticipantWasTyping,
  WsParticipantHadConversationOpen,
  WsNewConversation,
  NEW_CONVERSATION_EVENT,
  PasswordResetApi,
  SigninOptionsRequest,
  AuthenticationOptions,
  WsConversationReadStatus,
  CONVERSATION_READ_STATUS_EVENT,
  ConversationLabelsApi,
  CONVERSATION_UPDATED_EVENT,
  WsConversationUpdated,
  CONVERSATION_ARCHIVED_EVENT,
  WebhooksApi,
  MessageTemplatesApi,
  WorkflowsApi,
  WsConversationArchived,
  TenantColorConfigApi,
  UserPreferenceApi,
  WebhookFeaturesApi,
  TenantDomainsApi,
  WsParticipantCreated,
  PARTICIPANTS_CREATED_EVENT,
  TokenStorageConfig,
} from '@clientwire/api';
import { logger } from '../utils';
import { InjectionToken } from '@angular/core';
import { AppTypeService } from './app-type.service';

// TODO: this seems to leak into the global window object
export const WIRE_API_BASE_URL = new InjectionToken<string>('WIRE_API_BASE_URL');

@Injectable({
  providedIn: 'root',
})
export class WireApiService implements OnDestroy {
  #wireClient!: ClientWireApiClient;
  private authenticationErrorSubject = new Subject<void | { reason: string }>();

  // Modern dependency injection using inject() function
  private apiUrl = inject(WIRE_API_BASE_URL, { optional: true });
  private appTypeService = inject(AppTypeService, { optional: true });

  //#region Lifecycle

  constructor() {
    // Configure token storage based on app type
    const tokenStorageConfig = this.getTokenStorageConfig();

    this.#wireClient = new ClientWireApiClient(
      this.getApiUrl(this.apiUrl ?? undefined),
      logger.getLogLevel(),
      tokenStorageConfig
    );

    this.registerDefaultEvents();
  }

  private getTokenStorageConfig(): TokenStorageConfig {
    const appType = this.appTypeService?.type();

    switch (appType) {
      case 'client':
        // For client-app, use sessionStorage with app-specific prefix to isolate each instance
        return {
          storageType: 'sessionStorage',
          keyPrefix: 'wire-client-app',
        };
      case 'main':
        // For main-app, use localStorage with app-specific prefix
        return {
          storageType: 'localStorage',
          keyPrefix: 'wire-main-app',
        };
      case 'embed':
        // For embed-lib, use sessionStorage with app-specific prefix for isolation
        return {
          storageType: 'sessionStorage',
          keyPrefix: 'wire-embed-lib',
        };
      default:
        // Default to localStorage for backward compatibility
        return {
          storageType: 'localStorage',
        };
    }
  }

  ngOnDestroy(): void {
    this.authenticationErrorSubject.complete();
  }

  //#endregion

  //#region Events

  public subscribeToConversation(
    conversationId: string,
    callback: (
      payload:
        | WsNewMessage
        | WsMessageUpdated
        | WsParticipantReadStatus
        | WsParticipantWasTyping
        | WsConversationUpdated
        | WsParticipantHadConversationOpen
        | WsConversationArchived
        | WsParticipantCreated
    ) => void
  ): () => void {
    return this.#wireClient.on(`conversations:${conversationId}`, callback);
  }

  public subscribeToNewConversation(callback: (payload: WsNewConversation) => void): () => void {
    return this.#wireClient.on(NEW_CONVERSATION_EVENT, callback);
  }
  public subscribeToConversationParticipantsUpdated(
    callback: (payload: WsParticipantCreated) => void
  ): () => void {
    return this.#wireClient.on(PARTICIPANTS_CREATED_EVENT, callback);
  }

  public subscribeToConversationUpdates(
    callback: (payload: WsConversationUpdated) => void
  ): () => void {
    return this.#wireClient.on(CONVERSATION_UPDATED_EVENT, callback);
  }

  public subscribeToConversationArchived(
    callback: (payload: WsConversationArchived) => void
  ): () => void {
    return this.#wireClient.on(CONVERSATION_ARCHIVED_EVENT, callback);
  }

  public subscribeToConversationReadStatus(
    callback: (payload: WsConversationReadStatus) => void
  ): () => void {
    return this.#wireClient.on(CONVERSATION_READ_STATUS_EVENT, callback);
  }

  get onAuthenticationError$(): Observable<void | { reason: string }> {
    return this.authenticationErrorSubject.asObservable();
  }

  private registerDefaultEvents() {
    this.#wireClient.on(CONNECTED_EVENT, () => {
      logger.debug('Connected with backend');
    });
    this.#wireClient.on(DISCONNECTED_EVENT, () => {
      logger.debug('Disconnected from backend');
    });

    this.#wireClient.on(AUTHENTICATION_ERROR_EVENT, (event) => {
      logger.debug('Authentication error', event);
      this.authenticationErrorSubject.next(event?.reason ? { reason: event.reason } : undefined);
    });
  }

  //#endregion

  //#region Login/Logout

  public async signInWithEmailAndPassword(
    tenantId: string,
    email: string,
    password: string
  ): Promise<TokenResponseDto> {
    return await this.#wireClient.signInWithEmailAndPassword(tenantId, email, password);
  }

  // The Token Exchange methods don't reply with a refresh token, so they need a callback to get the token
  public async signInWithParticipantAuthKey(
    tenantId: string,
    authKey: string
  ): Promise<TokenResponseDto> {
    return await this.#wireClient.signInWithTokenExchangeParticipantAuthKey(
      tenantId,
      () => authKey
    );
  }

  // The Token Exchange methods don't reply with a refresh token, so they need a callback to get the token
  public async signInWithPassthroughToken(
    tenantId: string,
    token: string | (() => string) | (() => string | Promise<string>) | Promise<string>
  ): Promise<TokenResponseDto> {
    return await this.#wireClient.signInWithTokenExchangeAccessToken(tenantId, token);
  }

  public async signInWithStoredCredentials(): Promise<void> {
    await this.#wireClient.signInWithStoredCredentials();
  }

  public get hasCredentials(): boolean {
    return this.#wireClient.hasCredentials;
  }

  logout() {
    this.#wireClient.logout();
  }

  //#endregion

  //#region Helpers

  private getApiUrl(apiUrl?: string): string {
    if (apiUrl) {
      return apiUrl;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any).spaConfig?.endpoints?.api;
  }

  //#endregion

  //#region Make APIs available

  public get apiKeysApi(): APIKeysApi {
    return this.#wireClient.apiKeysApi;
  }

  public get conversationTypesApi(): ConversationTypesApi {
    return this.#wireClient.conversationTypesApi;
  }

  public get conversationsApi(): ConversationsApi {
    return this.#wireClient.conversationsApi;
  }

  public get geocodingApi(): GeocodingApi {
    return this.#wireClient.geocodingApi;
  }

  public get messagesApi(): MessagesApi {
    return this.#wireClient.messagesApi;
  }

  public get oidcConfigsApi(): OIDCConfigsApi {
    return this.#wireClient.oidcConfigsApi;
  }

  public get participantsApi(): ParticipantsApi {
    return this.#wireClient.participantsApi;
  }

  public get smsSettingsApi(): SMSSettingsApi {
    return this.#wireClient.smsSettingsApi;
  }

  public get signupApi(): SignupApi {
    return this.#wireClient.signupApi;
  }

  public get tenantConfigApi(): TenantConfigApi {
    return this.#wireClient.tenantConfigApi;
  }

  public get tenantsApi(): TenantsApi {
    return this.#wireClient.tenantsApi;
  }

  public get usersApi(): UsersApi {
    return this.#wireClient.usersApi;
  }

  public get userPreferencesApi(): UserPreferenceApi {
    return this.#wireClient.userPreferenceApi;
  }

  public get passwordResetApi(): PasswordResetApi {
    return this.#wireClient.passwordResetApi;
  }

  public get webhooksApi(): WebhooksApi {
    return this.#wireClient.webhooksApi;
  }

  public get webhookFeaturesApi(): WebhookFeaturesApi {
    return this.#wireClient.webhookFeaturesApi;
  }

  public async getSignInOptions(
    signInOptionsRequest: SigninOptionsRequest
  ): Promise<AuthenticationOptions> {
    return await this.#wireClient.getSignInOptions(signInOptionsRequest);
  }

  public get conversationLabelsApi(): ConversationLabelsApi {
    return this.#wireClient.conversationLabelsApi;
  }

  public get messageTemplatesApi(): MessageTemplatesApi {
    return this.#wireClient.messageTemplateApi;
  }
  public get workflowsApi(): WorkflowsApi {
    return this.#wireClient.workflowsApi;
  }

  public get tenantColorConfigApi(): TenantColorConfigApi {
    return this.#wireClient.tenantColorConfigApi;
  }

  public get tenantDomainsApi(): TenantDomainsApi {
    return this.#wireClient.tenantDomainsApi;
  }

  //#endregion
}
